import { Children, useState } from "react";
import {
  Chip,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  styled,
  IconButton,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { get, noop } from "lodash";
import { memo } from "utils/memo";
import T from "utils/constants/T";
import { PAGINATION } from "utils/constants/pagination";
import { useSelector } from "store";
import "./deductionTable.css";
import { formatAmount } from "utils/CommonMethods";

const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: "inherit",
  overflow: "hidden",
  padding: "5px 0px 5px 8px",
}));

interface DeductionTableBodyProps {
  page?: number;
  rowsPerPage?: number;
  totalTableRowsCount?: number;
  totalPageCount?: number;
  open?: boolean;
  handleExportViewDeduction?: any;
  handleExportViewTransaction?: any;
  deductionViewList?: any[];
  handleClick?: any;
  handleClose?: any;
  handleRunDeduction?: any;
  handleReverseDeduction?: any;
  anchorEl?: any;
  allTableRows: any[];
}
const DeductionTableBody = ({
  page = INITIAL_PAGE,
  rowsPerPage = ROWS_PER_PAGE,
  totalTableRowsCount = INITIAL_PAGE,
  totalPageCount = INITIAL_PAGE,
  anchorEl,
  open = false,
  handleExportViewDeduction = noop,
  handleExportViewTransaction = noop,
  deductionViewList = [],
  handleClick = noop,
  handleClose = noop,
  handleRunDeduction = noop,
  handleReverseDeduction = noop,
  allTableRows = [],
}: DeductionTableBodyProps) => {
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const [, setviewDeductions] = useState(false);
  const [statusDeduction, setStatusDeduction] = useState(false);
  const [downloadDeductions, setDownloadDeductions] = useState(false);
  const [viewRunDeductions, setViewRunDeductions] = useState(false);

  const selectedCompanyId = get(selectedCompany, "_id", "");
  const { systemPayrollCode , currencySymbol} = useSelector(
    (state: any) => state.companyTabData
  );
  const isReckon = systemPayrollCode === "RECKON";
  const isXero = systemPayrollCode === "XERO";
  const { loader } = useSelector((state: any) => state.deductionData);
  let cs = currencySymbol ?  currencySymbol : T.DOLLAR;

  const getColourClass = (deductionStatus: string) => {
    let status;
    if (deductionStatus === T.UNSENT) {
      status = "error";
    } else if (deductionStatus === T.SENT) {
      status = "success";
    } else {
      status = "info";
    }
    return status;
  }

  return (
    <TableBody>
      {loader ? (
        <TableRow>
          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.LOADING}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : allTableRows && allTableRows.length === 0 ? (
        <TableRow>
          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.NO_RECORDS}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : (
    <>
      {Children.toArray(
        allTableRows.map((record) => {
          const deductionStatus = get(record, "deduction_status", "");
          const withdrawalStatus = get(record, "withdrawal_status", "");
          const reckon_payrun_id = get(record, "reckon_payrun_id", "");
          const payPeriodId = get(record, "pay_period_id", "");
          const viewCompanyId = get(record, "company_id", "");
          const isPreventWithdrawals = get(record, "isPreventWithdrawals", "");

          // Map Reckon payrun status:
          // Draft (1) -> Available -> Green
          // Active -> Processing -> Yellow
          // Paid (8) -> Completed -> Blue
          // Deleted / Not Linked -> Not Available -> Red
          const getReckonStatus = () => {
            const statusMsg = get(record, "reckon_payrun_msg", "");
            const details = get(record, "reckon_payrun_details", null);

            if (!reckon_payrun_id || statusMsg === "Not Linked" || statusMsg === "Deleted") {
              return { label: "Not Available", color: "#ef4444" };
            }

            if (details) {
              const statusCode = get(details, "status_code");
              const statusText = get(details, "status");

              if (statusCode === 1 || statusText === "Draft") {
                return { label: "Available", color: "#4caf50" };
              }
              if (statusCode === 2 || statusText === "Active" || statusText === "Processing") {
                return { label: "Processing", color: "#f59e0b" };
              }
              if (statusCode === 8 || statusText === "Paid") {
                return { label: "Paid", color: "#2196f3" };
              }
              if (statusText === "Done") {
                return { label: "Done", color: "#2196f3" };
              }
            }

            // Fallback based on message/id
            if (statusMsg === "Draft") return { label: "Available", color: "#4caf50" };
            if (statusMsg === "Active" || statusMsg === "Processing") return { label: "Processing", color: "#f59e0b" };
            if (statusMsg === "Paid") return { label: "Paid", color: "#2196f3" };
            if (statusMsg === "Done") return { label: "Done", color: "#2196f3" };

            return {
              label: reckon_payrun_id ? "Available" : "Not Available",
              color: reckon_payrun_id ? "#4caf50" : "#ef4444",
            };
          };

          const getXeroStatus = () => {
            const statusMsg = get(record, "xero_payrun_msg", "");
            const status = get(record, "xero_payrun_status", "");

            if (status === "Available" || statusMsg === "DRAFT" || statusMsg === "Draft") {
              return { label: "Draft", chipColor: "success" };
            }
            return { label: "Posted", chipColor: "error" };
          };

          const reckonStatus = getReckonStatus();
          const xeroStatus = getXeroStatus();

          return (
            <TableRow sx={{ background: "white" }} key={`${record.srNo}-${record.amount}`}>
              {!selectedCompanyId && (
                <StyledTableCell width="200px">
                  {get(record, "company_name", "")}
                </StyledTableCell>
              )}
              <StyledTableCell width="200px">
                <Box className="table-column">
                  {get(record, "pay_periods", "")}
                </Box>
              </StyledTableCell>
              <StyledTableCell align="center">
                <Chip
                  color={getColourClass(deductionStatus) as any}
                  label={deductionStatus}
                  size="small"
                  variant="light"
                />
              </StyledTableCell>
              {systemPayrollCode !== "MYOBADVANCED" && selectedCompanyId && (
                <StyledTableCell>
                  {get(record, "payPeriodDetails.payPeriodName", "")}
                </StyledTableCell>
              )}
              {systemPayrollCode === "MYOBADVANCED" && selectedCompanyId && (
                <StyledTableCell align="center">
                  {get(record, "payGroupId", "N/A")}
                </StyledTableCell>
              )}
              {isXero && (
                <StyledTableCell align="center">
                  <Chip
                    color={xeroStatus.chipColor as any}
                    label={xeroStatus.label}
                    size="small"
                    variant="light"
                  />
                </StyledTableCell>
              )}
              {isReckon && (
                <StyledTableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: reckonStatus.color,
                      }}
                    />
                    {reckonStatus.label}
                  </Box>
                </StyledTableCell>
              )}
              <StyledTableCell align="center">
                {withdrawalStatus}
              </StyledTableCell>
              <StyledTableCell>
                <Box className="table-column">
                  {deductionStatus === T.UNSENT
                    ? "N/A"
                    : get(record, "date_time", "")}
                </Box>
              </StyledTableCell>
              <StyledTableCell>{`${cs}`+formatAmount(get(record, 'amount', ''))}</StyledTableCell>
              <StyledTableCell>
                <IconButton
                  aria-controls={"basic-menu"}
                  aria-haspopup="true"
                  onClick={(e: any) => {
                    handleClick(e, viewCompanyId, payPeriodId, record);
                    setStatusDeduction(
                      deductionStatus === T.SENT
                    );
                    setviewDeductions(!!isPreventWithdrawals);
                    setDownloadDeductions(
                      get(record, "amount", "") === 0
                    );
                    setViewRunDeductions(
                      get(record, "amount", "") === 0
                    );
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </StyledTableCell>
            </TableRow>
          );
        })
      )}

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => handleRunDeduction()}
          disabled={viewRunDeductions}
        >
          {statusDeduction ? T.VIEW_DEDUCTION : T.VIEW_RUN_DEDUCTION}
        </MenuItem>
        <MenuItem
          onClick={() => handleExportViewTransaction()}
          disabled={downloadDeductions}
        >
          {T.DOWNLOAD_TRANSACTIONS}
        </MenuItem>
        <MenuItem
          onClick={() => handleExportViewDeduction()}
          disabled={downloadDeductions}
        >
          {T.DOWNLOAD_DEDUCTION}
        </MenuItem>
      </Menu>
    </>
      )}
    </TableBody>
  );
};

export default memo(DeductionTableBody);
